<template>
	<div class="checkout-hotel-info-gallery">
		<div
			ref="swiperRef"
			class="checkout-hotel-info-gallery__container-outer"
		>
			<div>
				<swiper-container
					:slides-per-view="swiperOptions.slidesPerView"
					:navigation="swiperOptions.navigation"
					class="checkout-hotel-info-gallery__container-inner"
				>
					<swiper-slide
						v-for="(image, key, index) in images"
						:key="key"
						:index="index"
						class="checkout-hotel-info-gallery__slide"
					>
						<img
							class="checkout-hotel-info-gallery__image rpb_lazy"
							data-sizes="auto"
							:data-src="image.URL"
							src="data:image/gif;base64,R0lGODdhEgAMAIABAPD2/////ywAAAAAEgAMAAACDYSPqcvtD6OctNqLVQEAOw=="
							:alt="`${image.Caption} - ${key}`"
							@click="openGallery(index)"
						/>
					</swiper-slide>
				</swiper-container>
			</div>
			<div
				:id="`${uniqueId}-next`"
				class="swiper-button-next checkout-hotel-info-gallery__next-icon"
			>
				<FaIcon
					class="checkout-hotel-info-gallery__chevron-icon"
					:icon="['fa-solid', 'fa-chevron-right']"
				/>
			</div>
			<div
				:id="`${uniqueId}-prev`"
				class="swiper-button-prev checkout-hotel-info-gallery__prev-icon"
			>
				<FaIcon
					class="checkout-hotel-info-gallery__chevron-icon"
					:icon="['fa-solid', 'fa-chevron-left']"
				/>
			</div>
		</div>
		<GalleryModalWithGrid
			ref="gridModalRef"
			:focused-index="modalIndex"
			:gallery-items="imageArray"
			:header-title="hotelName"
		/>
	</div>
</template>

<script lang="ts" setup>
import { computed, ref, onActivated, useTemplateRef } from 'vue';
import { register } from 'swiper/element/bundle';
import type { HotelCatalogueImageType } from '@/interfaces/hotel-catalogue-types';
import { v4 as uuidv4 } from 'uuid';
import FaIcon from '@lmt-rpb/Atoms/FaIcon/FaIcon.vue';
import { type ClientType } from '@/interfaces/common';
import 'swiper/scss';
import GalleryModalWithGrid from '@lmt-rpb-new/molecules/GalleryModalNew/GalleryModalWithGrid.vue';
import { catalogueIconsIntoGalleryElements } from 'src/services/catalogueIcons';
import type { SwiperOptions } from 'swiper/types';

const props = withDefaults(
	defineProps<{
		images: Record<number, HotelCatalogueImageType>;
		client?: ClientType;
		hotelName?: string;
	}>(),
	{
		hotelName: '',
		client: 'at',
	}
);

const uniqueId = 'id-' + uuidv4();
const swiperRef = ref<HTMLElement>();
const modalIndex = ref(0);
const gridModalRef = useTemplateRef('gridModalRef');

register();

const swiperOptions: SwiperOptions = {
	slidesPerView: 'auto',
	navigation: {
		nextEl: `#${uniqueId}-next`,
		prevEl: `#${uniqueId}-prev`,
	},
};

const imageArray = computed(() =>
	catalogueIconsIntoGalleryElements(Object.values(props.images), `© GIATA GmbH 1996 - ${new Date().getFullYear()}`)
);

const initializeSwiper = () => {
	if (!swiperRef.value) return;

	const swiperEl = swiperRef.value.querySelector('swiper-container');
	if (!swiperEl) {
		console.error('Swiper container not found');
		return;
	}

	swiperEl.initialize();
};

onActivated(() => {
	initializeSwiper();
});

const toggleGallery = (index: number) => {
	modalIndex.value = index;
	if (gridModalRef.value) {
		gridModalRef.value.openGalleryGridModal();
	}
};

const openGallery = (index: number) => toggleGallery(index);
</script>

<style lang="scss" scoped>
.checkout-hotel-info-gallery {
	position: relative;
	background-color: $color-page-background;

	&__image {
		object-fit: cover;
		max-width: 100%;
		height: 100%;
	}

	&__slide {
		position: relative;
		cursor: pointer;
		margin-right: 0.8rem;
		display: inline-block;
		max-width: 30rem;
		height: 20rem;

		img {
			object-fit: cover;
			width: 100%;
		}

		@include media-query-up($breakpoint-small) {
			margin-right: 1.6rem;
			max-width: 37.5rem;
			height: 25rem;
		}
	}

	& .swiper-button-prev,
	& .swiper-button-next {
		position: absolute;
		top: 50%;
		transform: translateY(-50%);
		background: $color-white;
		border-radius: 50%;
		border: 0.1rem solid $color-primary;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 3.2rem;
		height: 3.2rem;
		z-index: 5;
		cursor: pointer;

		.checkout-hotel-info-gallery__chevron-icon {
			width: 1.5rem;
			font-size: 1.5rem;

			:deep(path) {
				fill: $color-primary;
			}

			@include media-query-up($breakpoint-small) {
				width: 2.2rem;
				font-size: $font-medium-2;
			}
		}

		@include media-query-up($breakpoint-small) {
			width: 4.8rem;
			height: 4.8rem;
		}
	}

	& .swiper-button-prev {
		left: 10px;
		opacity: 1;
		display: inital;
		transition: opacity 0.15s ease-in-out;

		.checkout-hotel-info-gallery__chevron-icon {
			color: $color-primary;
		}
	}

	& .swiper-button-next {
		right: 10px;
		display: inital;
		opacity: 1;
		transition: opacity 0.15s ease-in-out;

		.checkout-hotel-info-gallery__chevron-icon {
			color: $color-primary;
		}
	}

	.swiper-button-disabled {
		display: none;
		opacity: 0;
		transition: opacity 0.15s ease-in-out;
	}

	&__aria-label {
		@include sr-only;
	}
}
</style>
